
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download } from 'lucide-react';
import { useRoyaltyReport } from '@/hooks/useRoyaltyReports';
import { useQuarterlyReports } from '@/hooks/useQuarterlyReports';
import { useAuth } from '@/contexts/AuthContext';
import { usePDFGeneration } from '@/hooks/usePDFGeneration';
import { useToast } from '@/hooks/use-toast';
import ReportInformationCard from '@/components/royalty/ReportInformationCard';
import QuarterSummaryCard from '@/components/royalty/QuarterSummaryCard';
import BooksBreakdownTable from '@/components/royalty/BooksBreakdownTable';

const RoyaltyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleDownloadPDF } = usePDFGeneration();
  const { toast } = useToast();
  
  const { data: report, isLoading } = useRoyaltyReport(id || '');
  
  // Get reports for this specific quarter only
  const { data: quarterlyReports } = useQuarterlyReports(
    user?.id || '', 
    report?.year || 0, 
    report?.quarter || 0
  );
  
  const onDownloadPDF = async () => {
    if (!report) return;
    
    // Check if report status is draft
    if (report.status === 'draft') {
      toast({
        title: 'Download Not Available',
        description: 'PDF download is not available for draft reports. Please wait for the report to be published.',
        variant: 'destructive',
      });
      return;
    }

    await handleDownloadPDF(
      report,
      quarterlyReports,
      totalSales,
      totalRoyaltyAmount,
      totalGrossSales,
      0, // removed totalReturns
      totalPreviousRoyaltyIncome,
      report.profiles?.name || user?.email || 'Unknown Author'
    );
  };
  
  if (isLoading) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex justify-center items-center h-full">
          <p>Loading royalty report...</p>
        </div>
      </MainLayout>
    );
  }
  
  if (!report) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-2">Report Not Found</h2>
          <p className="mb-4">The royalty report you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/author/royalties')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
        </div>
      </MainLayout>
    );
  }

  // Filter quarterly reports to only include the specific quarter and year
  const currentQuarterReports = quarterlyReports?.filter(r => 
    r.quarter === report.quarter && r.year === report.year
  ) || [];

  // Calculate totals only for the current quarter using new logic
  const totalSales = currentQuarterReports.reduce((sum, r) => sum + (r.unitsSold || r.salesCount), 0) || report.unitsSold || report.salesCount;
  const totalGrossSales = currentQuarterReports.reduce((sum, r) => sum + (r.grossSales || r.amount), 0) || report.grossSales || report.amount;
  
  // Calculate total previous royalty income (using netSales field)
  const totalPreviousRoyaltyIncome = currentQuarterReports.reduce((sum, r) => sum + (r.netSales || 0), 0) || report.netSales || 0;
  
  // Calculate total royalty amount using new formula: Gross Sales + Previous Royalty Income
  const totalRoyaltyAmount = currentQuarterReports.reduce((sum, r) => sum + (r.royaltyAmount || r.amount), 0) || report.royaltyAmount || report.amount;
  
  const isDraftStatus = report.status === 'draft';
  
  return (
    <MainLayout allowedRoles={['author']}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/author/royalties')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <PageHeader
            title={`Q${report.quarter} ${report.year} Royalty Report`}
            description={`Report for ${report.books?.title || 'Unknown Book'}`}
          />
        </div>
        <Button 
          onClick={onDownloadPDF} 
          className="flex gap-2"
          disabled={isDraftStatus}
          variant={isDraftStatus ? "secondary" : "default"}
        >
          <Download className="h-4 w-4" />
          {isDraftStatus ? 'Draft - Download Unavailable' : 'Download PDF'}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <ReportInformationCard report={report} />
        <QuarterSummaryCard 
          currentQuarterReports={currentQuarterReports.length > 0 ? currentQuarterReports : [report]}
          totalSales={totalSales}
          totalRoyaltyAmount={totalRoyaltyAmount}
          currency={report.currency}
        />
      </div>
      
      <BooksBreakdownTable
        report={report}
        currentQuarterReports={currentQuarterReports}
        totalSales={totalSales}
        totalGrossSales={totalGrossSales}
        totalReturns={0}
        totalNetSales={totalPreviousRoyaltyIncome}
        totalRoyaltyAmount={totalRoyaltyAmount}
      />
    </MainLayout>
  );
};

export default RoyaltyDetail;
